import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { FiDownload, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import './SharedDocumentViewer.css';

const SharedDocumentViewer = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchSharedDocument();
    } else {
      setError('Missing token parameter');
      setLoading(false);
    }
  }, [token]);

  const fetchSharedDocument = async () => {
    if (!token) {
      setError('Invalid share token');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use plain axios without auth headers for public access
      // Token is passed as query parameter
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/documents/shared?token=${encodeURIComponent(token)}`,
        {
          responseType: 'blob',
          withCredentials: true,
          validateStatus: (status) => status >= 200 && status < 300
        }
      );

      // Get content type from response
      const contentType = response.headers['content-type'] || '';
      
      // Check if it's an error JSON response (backend might return JSON errors as blob)
      if (contentType.includes('application/json')) {
        try {
          const blobText = await response.data.text();
          const errorData = JSON.parse(blobText);
          throw new Error(errorData.message || errorData.error || 'Failed to access shared document');
        } catch (parseError) {
          // If it's already our error, re-throw it
          if (parseError.message && parseError.message.includes('Failed to access')) {
            throw parseError;
          }
          // Otherwise, treat as unexpected error
          throw new Error('Failed to access shared document');
        }
      }

      // Store the blob (clone it if needed for multiple uses)
      const blob = response.data;
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'document';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
        }
      }

      // Determine file type
      const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(fileExtension);
      const isPdf = fileExtension === 'pdf';
      
      // Get permission from response header
      // Backend should send this in header: x-share-permission, x-permission, etc.
      const permission = response.headers['x-share-permission'] || 
                         response.headers['x-permission'] || 
                         'view';

      setFileType({
        extension: fileExtension,
        isImage,
        isPdf
      });

      setDocumentData({
        fileName,
        permission,
        blob: blob,
        contentType
      });

      // For view permission: create preview URL
      if (permission === 'view' || permission === 'read') {
        const blobUrl = URL.createObjectURL(blob);
        setPreviewUrl(blobUrl);
      } else if (permission === 'download') {
        // For download permission, trigger download immediately
        setDownloading(true);
        setTimeout(() => {
          triggerDownload(blob, fileName);
        }, 500);
      }
    } catch (err) {
      console.error('Error fetching shared document:', err);
      
      // Try to parse error message from blob response
      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const errorData = JSON.parse(errorText);
          setError(errorData.message || errorData.error || 'Failed to access shared document');
        } catch (parseError) {
          setError('Failed to access shared document');
        }
      } else {
        const errorMessage = err.response?.data?.message || 
                            err.response?.data?.error || 
                            err.message || 
                            'Failed to access shared document. The link may be expired or revoked.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const triggerDownload = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    setDownloading(false);
  };

  const handleDownload = () => {
    if (!documentData) return;
    
    setDownloading(true);
    triggerDownload(documentData.blob, documentData.fileName);
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (loading) {
    return (
      <div className="shared-document-viewer-container">
        <Container className="text-center py-5">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading shared document...</p>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-document-viewer-container">
        <Container className="py-5">
          <Alert variant="danger" className="text-center">
            <FiAlertCircle size={48} className="mb-3" />
            <h4>Access Denied</h4>
            <p>{error}</p>
            <div className="mt-3">
              <Button variant="primary" onClick={() => window.location.href = '/'}>
                <FiArrowLeft className="me-2" />
                Go to Home
              </Button>
            </div>
          </Alert>
        </Container>
      </div>
    );
  }

  if (!documentData) {
    return null;
  }

  const { fileName, permission } = documentData;
  const { isImage, isPdf } = fileType;

  return (
    <div className="shared-document-viewer-container">
      <Container fluid className="p-0">
        {/* Header */}
        <div className="shared-document-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">{fileName}</h5>
            <small className="text-muted">Shared Document</small>
          </div>
          {permission === 'view' || permission === 'read' ? (
            <Button
              variant="primary"
              onClick={handleDownload}
              disabled={downloading}
            >
              <FiDownload className="me-2" />
              {downloading ? 'Downloading...' : 'Download'}
            </Button>
          ) : null}
        </div>

        {/* Content Area */}
        <div className="shared-document-content">
          {permission === 'download' ? (
            <div className="text-center py-5">
              <p className="text-muted">File download initiated...</p>
              <p className="text-muted">If download doesn't start, click below:</p>
              <Button variant="primary" onClick={handleDownload} disabled={downloading}>
                <FiDownload className="me-2" />
                {downloading ? 'Downloading...' : 'Download Again'}
              </Button>
            </div>
          ) : previewUrl ? (
            <div className="shared-document-preview">
              {isPdf ? (
                <iframe
                  src={`${previewUrl}#toolbar=0`}
                  style={{
                    width: '100%',
                    height: 'calc(100vh - 120px)',
                    border: 'none'
                  }}
                  title="PDF Preview"
                />
              ) : isImage ? (
                <div className="text-center p-4">
                  <img
                    src={previewUrl}
                    alt={fileName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 'calc(100vh - 200px)',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted">Preview not available for this file type.</p>
                  <Button variant="primary" onClick={handleDownload} disabled={downloading}>
                    <FiDownload className="me-2" />
                    {downloading ? 'Downloading...' : 'Download to View'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">Unable to display document.</p>
              <Button variant="primary" onClick={handleDownload} disabled={downloading}>
                <FiDownload className="me-2" />
                {downloading ? 'Downloading...' : 'Download'}
              </Button>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default SharedDocumentViewer;
